import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import RpmDetailsIcon from 'components/atoms/icons/rpmDetailsIcon';
import RpmCcmDetailsIcon from 'components/atoms/icons/rpmCcmDetailsIcon';
import ArrowLeftRightIcon from 'components/atoms/icons/arrow-left-right';

import s from './styles.module.scss';
import { formatNumber } from './utils';

import type { IProviderDashboardRpmDetails } from './types';

interface IRpmDetailsCardProps {
  data?: IProviderDashboardRpmDetails;
  isLoading?: boolean;
}

export const RpmDetailsCard: React.FC<IRpmDetailsCardProps> = ({
  data,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const totalBillableCodes = data?.totalBillableCodes;
  const totalReadingReviewed = data?.totalReadingReviewed;

  const billableCodeItems = [
    {
      labelKey: 'labels.code99453',
      total: totalBillableCodes?.[99453] ?? 0,
    },
    {
      labelKey: 'labels.code99454',
      total: totalBillableCodes?.[99454] ?? 0,
    },
    {
      labelKey: 'labels.code99470',
      total: totalBillableCodes?.[99470] ?? 0,
    },
    {
      labelKey: 'labels.code99457',
      total: totalBillableCodes?.[99457] ?? 0,
    },
    {
      labelKey: 'labels.code99458_40Min',
      total: totalBillableCodes?.['99458_40'] ?? 0,
    },
    {
      labelKey: 'labels.code99458_60Min',
      total: totalBillableCodes?.['99458_60'] ?? 0,
    },
    {
      labelKey: 'labels.code99458_80Min',
      total: totalBillableCodes?.['99458_80'] ?? 0,
    },
    {
      labelKey: 'labels.code99445',
      total: totalBillableCodes?.[99445] ?? 0,
    },
  ];

  return (
    <div className={s.reportCard}>
      <div className={s.reportCardHeader}>
        <span className={s.reportCardIcon}>
          <RpmDetailsIcon />
        </span>
        <span>{t('labels.rpmDetails')}</span>
      </div>
      <BaseSpinWrapper spinning={isLoading}>
        <div className={s.reportCardBody}>
          <div className={s.detailSection}>
            <h4 className={s.detailSubtitle}>
              {t('labels.totalBillableCodes')}
            </h4>
            <div className={s.codeTableRow}>
              <div className={s.codeTableGroup}>
                {billableCodeItems.map((item) => (
                  <div key={item.labelKey} className={s.codeTableCell}>
                    <div className={s.codeTableHeader}>
                      <RpmCcmDetailsIcon />
                      <span>{t(item.labelKey as never)}</span>
                    </div>
                    <div className={s.codeTableValueBlock}>
                      <div className={s.codeTableValueGreen}>
                        {formatNumber(item.total)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={s.detailSection}>
            <h4 className={s.detailSubtitle}>{t('labels.totalReadings')}</h4>
            <div className={s.readingsReviewedRow}>
              <div className={s.readingsReviewedBlock}>
                <div className={s.readingsReviewedHeaderRow}>
                  <div className={s.readingsReviewedHeaderCell}>
                    {t('labels.normal')}
                  </div>
                  <div className={s.readingsReviewedHeaderCell}>
                    {t('labels.atRisk')}
                  </div>
                  <div className={s.readingsReviewedHeaderCell}>
                    {t('labels.critical')}
                  </div>
                  <div className={s.readingsReviewedHeaderCell}>
                    {t('labels.total')}
                  </div>
                </div>
                <div className={s.readingsReviewedDataRow}>
                  <div className={s.readingsReviewedDataCell}>
                    <span className={s.readingDotGreen} />
                    <span>
                      {formatNumber(totalReadingReviewed?.normal ?? 0)}
                    </span>
                  </div>
                  <div className={s.readingsReviewedDataCell}>
                    <span className={s.readingDotOrange} />
                    <span>
                      {formatNumber(totalReadingReviewed?.atRisk ?? 0)}
                    </span>
                  </div>
                  <div className={s.readingsReviewedDataCell}>
                    <span className={s.readingDotRed} />
                    <span>
                      {formatNumber(totalReadingReviewed?.critical ?? 0)}
                    </span>
                  </div>
                  <div className={s.readingsReviewedDataCell}>
                    <span className={s.readingDotGrey} />
                    <span>
                      {formatNumber(totalReadingReviewed?.total ?? 0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className={s.readingsReviewedBlock}>
                <div className={s.readingsReviewedHeaderRow}>
                  <div className={s.readingsReviewedHeaderCell}>
                    <ArrowLeftRightIcon color="#667180" />
                    <span>{t('labels.twoWaySyncComplete')}</span>
                  </div>
                </div>
                <div className={s.readingsReviewedDataRow}>
                  <div className={s.readingsReviewedDataCell}>
                    <span>
                      {formatNumber(
                        totalReadingReviewed?.twoWaySyncCompleted ?? 0,
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BaseSpinWrapper>
    </div>
  );
};
